import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    OnChanges,
    Component,
    SimpleChanges,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    assignModelOptions,
    BooleanModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ConfirmationEquipmentModel,
    DateModel,
    DisplayDecorator,
    EventService,
    ODataModelChangeEvent,
    TimeModel,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import moment, { Duration } from 'moment';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'pace-confirmation-equipment',
    templateUrl: './confirmation-equipment.component.html',
    styleUrls: ['./confirmation-equipment.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationEquipmentComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public model: ConfirmationEquipmentModel;

    @Input()
    public parentForm: FormArray;

    private destroy$ = new Subject<void>();

    // form
    public equipmentForm: FormGroup;
    public equipmentFormChildren: FormArray;

    public displayStartDownTime = false;
    public readonly displayStatusAfterFix: boolean;
    public readonly displayLeaveTimeAndDateOfRecoveryCheckbox: boolean;
    public readonly technicalIdOrFunctionalLocationLabel: string;
    public readonly isMaterialBillingIndicatorEnabled: boolean;
    public leaveTimeOfRecovery: DisplayDecorator<BooleanModel>;
    public showQualityControlQuestions = false;
    public displayTimeAndDateOfRecovery = true;
    private isMaterialRequiringCommercialInfo: boolean;
    private isTimeRecordingRequiringCommercialInfo: boolean;
    public displayDowntimeDuration = false;
    public downtimeDuration: string;
    public downtimeDurationExceeded: boolean;
    public durationDifference: Duration;
    public constructor(
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly eventService: EventService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly configService: ConfigService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.displayStartDownTime = this.clientManager.isNgmP58();
        this.displayStatusAfterFix = this.clientManager.isNgmP58();
        this.displayLeaveTimeAndDateOfRecoveryCheckbox = this.clientManager.isNgmAP0();
        this.technicalIdOrFunctionalLocationLabel = this.clientManager.isNgmAP0()
            ? <string>this.translate.instant('CONFIRMATION_EQUIPMENT_FL_LABEL')
            : <string>this.translate.instant('CONFIRMATION_EQUIPMENT_TID_LABEL');
        this.showQualityControlQuestions = this.clientManager.isNgmAA();
        this.isMaterialBillingIndicatorEnabled = this.configService.getEnableBillingIndicator();
    }

    public ngOnInit(): void {
        if (this.isMaterialBillingIndicatorEnabled) {
            this.eventService.materialBillingIndicatorsNotReceived$
                .pipe(
                    tap((materials) => {
                        let isBillingIndicatorNotReceived = false;
                        if (materials.length > 0) {
                            isBillingIndicatorNotReceived = materials.some(
                                (material) =>
                                    material.billableCode.selectedEntry?.value === ConfigConstants.BILLABLE_CODE.AUTO &&
                                    material.billableCode.options.disabled,
                            );
                        }
                        this.setCommercialInfoTextForBillingIndicators(isBillingIndicatorNotReceived);
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
        this.eventService.materialBillableOptionChanged$
            .pipe(
                tap((isCommercialInfoMandatory) => {
                    this.isMaterialRequiringCommercialInfo = isCommercialInfoMandatory;
                    this.validateCommercialInfo();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
        this.eventService.timeRecordingBillableOptionChanged$
            .pipe(
                tap((isCommercialInfoMandatory) => {
                    this.isTimeRecordingRequiringCommercialInfo = isCommercialInfoMandatory;
                    this.validateCommercialInfo();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
        this.eventService.updateTimeOfRecoveryMaxValue$
            .pipe(
                tap((maxValueForTimeOfRecovery) => {
                    this.model.setTimeOptions(maxValueForTimeOfRecovery);
                    this.updateFormValidity();
                    this.changeDetector.markForCheck();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
        this.eventService.appointmentSlotBooked$
            .pipe(
                distinctUntilChanged(),
                tap((isSlotBooked) => {
                    this.model.isAppointmentSlotBooked = isSlotBooked;
                }),
            )
            .subscribe();
        if (this.clientManager.isNgmAP0()) {
            const isInvalidTimeOfRecovery = !this.model.timeOfRecoveryDate.value.isValid();
            this.leaveTimeOfRecovery = new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_EQUIPMENT_TIME_OF_RECOVERY_CHECKBOX'),
                new BooleanModel('', isInvalidTimeOfRecovery),
            );
            if (this.clientManager.isNgmAAANZ()) {
                this.eventService.followupOptionChanged$
                    .pipe(
                        tap((followupOptionYes) => {
                            // If the follow up is forced change
                            // PACE shall force the user to enter the infoToCCC in Equipment screen
                            this.model.infoToCCC = assignModelOptions(
                                this.model.infoToCCC,
                                {
                                    ...this.model.infoToCCC.options,
                                    required: followupOptionYes,
                                },
                                this.model,
                            );
                            this.updateFormValidity();
                            this.changeDetector.markForCheck();
                        }),
                        takeUntil(this.destroy$),
                    )
                    .subscribe();
            }
        }

        this.equipmentFormChildren = this.formBuilder.array([]);
        this.equipmentForm = this.formBuilder.group({
            children: this.equipmentFormChildren,
        });

        this.parentForm.push(this.equipmentForm);
        this.updateFieldsOnInitialization();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.model && !changes.model.firstChange) {
            this.updateFieldsOnInitialization();
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public updateFieldsOnInitialization(): void {
        if (this.clientManager.isNgmP58()) {
            this.updateTimeOfRecoveryFields();
        }
        if (this.clientManager.isNgmAP0()) {
            this.updateTimeOfRecoveryFieldsForAP0();
        }
    }

    public validateCommercialInfo(isBillingIndicatorsNotReceived = false): void {
        // 1. PACE shall force the user to enter the Commercial Info (BK_INFO_TEXT) in Equipment screen:
        //    if any of Material or Time recording is 'Billable' or 'Courtesy' (except when indicators are to be received from P58)
        // 2. If indicators are to be received from P58, and for some reason not received, then prefill commercial info text
        // 3. if there are commercialInfoCodes selected then commercialInfoDesc should be mandatory
        // 4. if there are no commercialInfoCodes selected then commercialInfoDesc should not be mandatory
        const billingIndicatorsNotReceived = this.isMaterialBillingIndicatorEnabled && isBillingIndicatorsNotReceived;
        const bkInfoTextForBillingIndicatorsNotReceived =
            this.configService.getBKInfoTextForBillingIndicatorsNotReceived();
        this.model.commercialInfoDesc = assignModelOptions(
            this.model.commercialInfoDesc,
            {
                ...this.model.commercialInfoDesc.options,
                required:
                    this.model.commercialInfoCodes.selectedEntries.length > 0 ||
                    this.isMaterialRequiringCommercialInfo ||
                    (billingIndicatorsNotReceived && bkInfoTextForBillingIndicatorsNotReceived.length > 0) ||
                    this.isTimeRecordingRequiringCommercialInfo,
            },
            this.model,
        );

        this.updateFormValidity();
        this.changeDetector.detectChanges();
    }

    private setCommercialInfoTextForBillingIndicators(isBillingIndicatorsNotReceived: boolean): void {
        const bkInfoTextForBillingIndicatorsNotReceived =
            this.configService.getBKInfoTextForBillingIndicatorsNotReceived();
        if (
            isBillingIndicatorsNotReceived &&
            bkInfoTextForBillingIndicatorsNotReceived.length > 0 &&
            !this.model.commercialInfoDesc.value.includes(bkInfoTextForBillingIndicatorsNotReceived)
        ) {
            this.model.commercialInfoDesc.value =
                this.model.commercialInfoDesc.value.length > 0
                    ? `${this.model.commercialInfoDesc.value} ${bkInfoTextForBillingIndicatorsNotReceived}`
                    : bkInfoTextForBillingIndicatorsNotReceived;
        } else if (!isBillingIndicatorsNotReceived) {
            this.model.commercialInfoDesc.value = this.model.commercialInfoDesc.value.replace(
                bkInfoTextForBillingIndicatorsNotReceived,
                '',
            );
        }
        this.validateCommercialInfo(isBillingIndicatorsNotReceived);
    }

    public updateFormValidity(shouldEmitEvent = false): void {
        this.equipmentFormChildren.controls.forEach((control) => {
            control.updateValueAndValidity({ emitEvent: shouldEmitEvent });
        });
    }

    public onTimeOfRecoveryChange(timeOfRecoveryChangeEvent: ODataModelChangeEvent<DateModel | TimeModel>): void {
        // to ensure both date and time model share the same reference
        this.model.timeOfRecoveryDate.value = timeOfRecoveryChangeEvent.newValue;
        this.model.timeOfRecoveryTime.value = timeOfRecoveryChangeEvent.newValue;
        this.setTimeOptions();
    }

    public setTimeOptions(): void {
        this.model.setTimeOptions();
        if (this.displayDowntimeDuration) {
            this.updateDowntimeDuration();
        }
        this.updateFormValidity();
        this.changeDetector.detectChanges();
    }

    private updateDowntimeDuration(): void {
        const downTimeStartTime = this.model.downTimeStartTime.value;
        const timeDifference = this.model.timeOfRecoveryTime.value.diff(downTimeStartTime, 'minutes');
        this.durationDifference = moment.duration(timeDifference, 'minutes');

        this.downtimeDuration = <string>this.translate.instant('CONFIRMATION_EQUIPMENT_DOWNTIME_DURATION', {
            days: Math.floor(this.durationDifference.asDays()),
            hours: this.durationDifference.hours(),
            minutes: this.durationDifference.minutes(),
        });
        const isDurationExceeded = timeDifference > this.configService.getDowntimeThresholdInMinutes();
        if (typeof this.downtimeDurationExceeded !== 'undefined') {
            this.model.downtimeDurationAcknowledged.value = !isDurationExceeded;
            this.model.downtimeDurationAcknowledged = cloneDeep(this.model.downtimeDurationAcknowledged);
        }

        this.model.downtimeDurationAcknowledged.model = assignModelOptions(
            this.model.downtimeDurationAcknowledged.model,
            {
                ...this.model.downtimeDurationAcknowledged.model.options,
                required: isDurationExceeded,
            },
            this.model,
        );
        this.updateDowntimeDurationValidation();
    }

    public updateDowntimeDurationValidation(): void {
        this.downtimeDurationExceeded = this.model.downtimeDurationAcknowledged.model.options.required;
    }

    /**
     * Sync Time of Recovery with Down Start
     */
    public syncDateTime(): void {
        const syncedDateTime = moment(this.model.downTimeStartDate.value);
        this.model.timeOfRecoveryDate.value = syncedDateTime;
        this.model.timeOfRecoveryTime.value = syncedDateTime;
        this.setTimeOptions();
    }

    /**
     * Called from markup.
     * 1. For P58: enforces followup if statusAfterFix is 'Totally down' or 'Partially down'
     * 2. Implements display/hide and setting value for time of recovery fields based on following
     * a. For P58, if statusAfterFix === 'Totally down', then set to invalid time, and hide, else display with current
     *    datetime value
     * b. For NGMAA, if leaveTimeOfRecovery is checked(true), then set to invalid time, and display as readonly, else
     *    display with current datetime value
     */
    public updateTimeOfRecoveryFields(): void {
        const statusInArrival = this.model.statusInArrival.selectedEntry?.value;
        const statusAfterFix = this.model.statusAfterFix.selectedEntry?.value;
        const working = ConfigConstants.EQUIPMENT_STATUS.WORKING;
        const totallyDown = ConfigConstants.EQUIPMENT_STATUS.TOTALLY_DOWN;

        if (this.model.isCriticalityChangeEnabled) {
            this.model.updateApproverVisibility();
        }

        if (this.model.hideRecoveryTime) {
            this.displayStartDownTime = !(statusInArrival === working && statusAfterFix === working);
        }

        if (statusAfterFix) {
            const isEquipmentTotallyDownAfterFix = statusAfterFix === totallyDown;
            this.displayTimeAndDateOfRecovery = !isEquipmentTotallyDownAfterFix;
            if (this.model.hideRecoveryTime) {
                if (statusInArrival === working && statusAfterFix === working) {
                    this.displayTimeAndDateOfRecovery = false;
                    this.model.timeOfRecoveryDate.value = this.model.downTimeStartDate.value.clone();
                    this.model.timeOfRecoveryTime.value = this.model.downTimeStartTime.value.clone();
                }
            }
            const timeOfRecovery: moment.Moment = isEquipmentTotallyDownAfterFix
                ? moment.invalid()
                : this.model.timeOfRecoveryTime.value.isValid()
                  ? this.model.timeOfRecoveryTime.value
                  : moment().startOf('minutes');
            this.model.timeOfRecoveryDate.value = timeOfRecovery;
            this.model.timeOfRecoveryTime.value = timeOfRecovery;
            this.displayDowntimeDuration =
                this.configService.getDowntimeThresholdInMinutes() !== -1 && this.displayTimeAndDateOfRecovery;
            this.eventService.triggerEquipmentStatusAfterFixChanged(this.model.statusAfterFix.selectedEntry);
        } else {
            this.displayTimeAndDateOfRecovery = false;
        }
        this.setTimeOptions();
    }

    /**
     * Called from markup.
     * For SA: enforce followup if leaveTimeOfRecovery is checked(true)
     * and StatusInArrival is totally or partially down
     */
    public updateStatusInArrivalFields(): void {
        if (this.model.hideRecoveryTime) {
            this.updateTimeOfRecoveryFields();
        }
        if (this.clientManager.isNgmSA()) {
            this.eventService.triggerEquipmentStatusInArrivalChanged({
                equipmentInArrival: this.model.statusInArrival.selectedEntry,
                timeOfRecoveryDate: this.model.timeOfRecoveryDate,
            });
        }
    }

    /**
     * Called from markup.
     * For AP0, if leaveTimeOfRecovery is checked(true), then set to invalid time, and display as readonly, else
     * display with current datetime value
     */
    public updateTimeOfRecoveryFieldsForAP0(): void {
        const now = moment().startOf('minutes');
        this.model.timeOfRecoveryDate.value = this.leaveTimeOfRecovery.value
            ? moment.invalid()
            : this.model.timeOfRecoveryDate.value.isValid()
              ? this.model.timeOfRecoveryDate.value
              : now;
        // if the time of recovery checkbox is unchecked then show the last saved valid time else current time
        this.model.timeOfRecoveryTime.value = this.leaveTimeOfRecovery.value
            ? moment.invalid()
            : this.model.timeOfRecoveryTime.value.isValid()
              ? this.model.timeOfRecoveryTime.value
              : now;
        this.model.timeOfRecoveryDate.options = {
            ...this.model.timeOfRecoveryDate.options,
            readonly: !!this.leaveTimeOfRecovery.value,
        };
        this.model.timeOfRecoveryTime.options = {
            ...this.model.timeOfRecoveryTime.options,
            readonly: !!this.leaveTimeOfRecovery.value,
        };

        this.setTimeOptions();
        this.validateForm();
        this.updateStatusInArrivalFields();
    }

    public validateForm(): void {
        this.changeDetector.detectChanges();
    }
}
